import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const PhoneVerification = () => {
  const { user, setUser } = useAuth();
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");

  const hasPhone = !!user?.phone_number;
  const verified = !!user?.phone_verified;

  if (verified) {
    return (
      <div data-testid="phone-verified-badge"
        className="flex items-center gap-2 border border-emerald-500/40 bg-emerald-500/10 px-3 py-2.5 rounded-sm">
        <ShieldCheck className="w-4 h-4 text-emerald-500 shrink-0" />
        <p className="text-xs text-emerald-200/90">Your phone number is verified. The call-in line is unlocked.</p>
      </div>
    );
  }

  const sendCode = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/auth/phone/send-code");
      if (data.already_verified) {
        toast.success("Your phone is already verified");
        return;
      }
      setSent(true);
      toast.success(data.message || "Verification code sent");
    } catch (err) {
      if (err.response?.status === 429) {
        setSent(true);
        toast.message("If you already received a code, enter it below.");
      } else {
        toast.error(err.response?.data?.detail || "Could not send code");
      }
    } finally {
      setSending(false);
    }
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post("/auth/phone/verify", { code: code.trim() });
      setUser(data);
      setSent(false);
      setCode("");
      toast.success("Phone number verified — the call-in line is now unlocked");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not verify code");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div data-testid="phone-verification" className="space-y-3">
      <div className="flex items-start gap-2 border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 rounded-sm">
        <ShieldAlert className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
        <p className="text-xs text-amber-200/90 leading-relaxed">
          Your phone number is not verified yet. Verify it to unlock the paid call-in line.
          We'll email a 6-digit code to <span className="font-mono text-amber-100">{user?.email}</span>.
        </p>
      </div>

      {!sent ? (
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={sendCode} disabled={!hasPhone || sending} data-testid="send-phone-code-btn"
            className="bg-[#007AFF] hover:bg-[#0066d6] text-white rounded-sm gap-2 font-semibold">
            <MailCheck className="w-4 h-4" /> {sending ? "Sending…" : "Send verification code"}
          </Button>
          <button type="button" onClick={() => setSent(true)} disabled={!hasPhone}
            data-testid="have-code-link"
            className="text-xs font-semibold text-[#007AFF] hover:underline disabled:opacity-40">
            I already have a code
          </button>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="6-digit code" inputMode="numeric" maxLength={6} data-testid="phone-code-input"
            className="bg-background border-border rounded-sm font-mono tracking-[0.4em] text-center max-w-[10rem]" />
          <div className="flex gap-2">
            <Button onClick={verify} disabled={code.length < 4 || verifying} data-testid="verify-phone-btn"
              className="bg-[#007AFF] hover:bg-[#0066d6] text-white rounded-sm font-semibold">
              {verifying ? "Verifying…" : "Verify"}
            </Button>
            <Button onClick={sendCode} disabled={sending} variant="outline" data-testid="resend-phone-code-btn"
              className="rounded-sm border-border text-muted-foreground hover:text-white">
              Resend
            </Button>
          </div>
        </div>
      )}
      {!hasPhone && (
        <p className="text-xs text-muted-foreground">Add and save a phone number above first.</p>
      )}
    </div>
  );
};
